import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Timer as TimerIcon,
  Coffee as CoffeeIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, differenceInDays } from 'date-fns';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function LeaveRequests() {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState({ open: false, request: null, action: '' });
  const [onBreak, setOnBreak] = useState(false);
  const [breakType, setBreakType] = useState('lunch');
  
  const [formData, setFormData] = useState({
    leave_type: 'vacation',
    start_date: null,
    end_date: null,
    reason: ''
  });

  const isAdmin = user?.roles?.includes('admin') || user?.roles?.includes('super_admin');

  useEffect(() => {
    loadLeaveRequests();
    checkBreakStatus();
  }, []);

 
const loadLeaveRequests = async () => {
  try {
    setLoading(true);
    const response = await api.get('/attendance/leave-requests');
    console.log('Leave requests response:', response.data); // Debug log
    
    // Handle different response formats
    if (response.data && Array.isArray(response.data)) {
      setLeaveRequests(response.data);
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      setLeaveRequests(response.data.data);
    } else {
      console.error('Unexpected response format:', response.data);
      setLeaveRequests([]);
    }
  } catch (error) {
    console.error('Error loading leave requests:', error);
    if (error.response) {
      console.error('Error response:', error.response.data);
    }
    setLeaveRequests([]);
  } finally {
    setLoading(false);
  }
};


  const checkBreakStatus = async () => {
    try {
      const response = await api.get('/attendance/break-status');
      setOnBreak(response.data.onBreak || false);
    } catch (error) {
      console.error('Error checking break status:', error);
    }
  };

  const handleSubmitLeave = async () => {
    try {
      await api.post('/attendance/leave-request', {
        ...formData,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        end_date: format(formData.end_date, 'yyyy-MM-dd')
      });
      
      setOpenDialog(false);
      setFormData({
        leave_type: 'vacation',
        start_date: null,
        end_date: null,
        reason: ''
      });
      loadLeaveRequests();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      alert(error.response?.data?.message || 'Error submitting leave request');
    }
  };

  const handleApproveReject = async () => {
    try {
      await api.put(`/attendance/leave-request/${approvalDialog.request.id}`, {
        status: approvalDialog.action,
        remarks: approvalDialog.remarks
      });
      
      setApprovalDialog({ open: false, request: null, action: '', remarks: '' });
      loadLeaveRequests();
    } catch (error) {
      console.error('Error updating leave request:', error);
    }
  };

  const handleStartBreak = async () => {
    try {
      await api.post('/attendance/break/start', { break_type: breakType });
      setOnBreak(true);
    } catch (error) {
      console.error('Error starting break:', error);
      alert(error.response?.data?.message || 'Error starting break');
    }
  };

  const handleEndBreak = async () => {
    try {
      await api.post('/attendance/break/end');
      setOnBreak(false);
    } catch (error) {
      console.error('Error ending break:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getLeaveTypeIcon = (type) => {
    switch (type) {
      case 'sick': return '🏥';
      case 'vacation': return '🏖️';
      case 'personal': return '👤';
      case 'emergency': return '🚨';
      default: return '📅';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Leave & Break Management
      </Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Leave Requests" />
        <Tab label="Break Management" />
      </Tabs>

      {/* Leave Requests Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            New Leave Request
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Days</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                {isAdmin && <TableCell>Actions</TableCell>}
              

</TableRow>
</TableHead>


<TableBody>
  {loading ? (
    <TableRow>
      <TableCell colSpan={isAdmin ? 8 : 7} align="center">
        Loading...
      </TableCell>
    </TableRow>
  ) : !leaveRequests || leaveRequests.length === 0 ? (
    <TableRow>
      <TableCell colSpan={isAdmin ? 8 : 7} align="center">
        No leave requests found
      </TableCell>
    </TableRow>
  ) : (
    leaveRequests.map((request) => (
      <TableRow key={request.id}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{getLeaveTypeIcon(request.leave_type)}</span>
            {request.leave_type}
          </Box>
        </TableCell>
        <TableCell>{request.full_name || request.username}</TableCell>
        <TableCell>{format(new Date(request.start_date), 'MMM dd, yyyy')}</TableCell>
        <TableCell>{format(new Date(request.end_date), 'MMM dd, yyyy')}</TableCell>
        <TableCell>
          {differenceInDays(new Date(request.end_date), new Date(request.start_date)) + 1}
        </TableCell>
        <TableCell>{request.reason}</TableCell>
        <TableCell>
          <Chip
            label={request.status}
            color={getStatusColor(request.status)}
            size="small"
          />
        </TableCell>
        {isAdmin && (
          <TableCell>
            {request.status === 'pending' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => setApprovalDialog({
                    open: true,
                    request,
                    action: 'approved'
                  })}
                >
                  <ApproveIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setApprovalDialog({
                    open: true,
                    request,
                    action: 'rejected'
                  })}
                >
                  <RejectIcon />
                </IconButton>
              </Box>
            )}
          </TableCell>
        )}
      </TableRow>
    ))
  )}
</TableBody>


 </Table>
        </TableContainer>
      </TabPanel>

      {/* Break Management Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Break Control
              </Typography>
              
              {!onBreak ? (
                <Box>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Break Type</InputLabel>
                    <Select
                      value={breakType}
                      label="Break Type"
                      onChange={(e) => setBreakType(e.target.value)}
                    >
                      <MenuItem value="lunch">Lunch Break</MenuItem>
                      <MenuItem value="tea">Tea Break</MenuItem>
                      <MenuItem value="personal">Personal Break</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    color="warning"
                    startIcon={<CoffeeIcon />}
                    onClick={handleStartBreak}
                  >
                    Start Break
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You are currently on a break
                  </Alert>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<TimerIcon />}
                    onClick={handleEndBreak}
                  >
                    End Break
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Break Policy
              </Typography>
              <Typography variant="body2" color="textSecondary">
                • Lunch Break: 1 hour (12:00 PM - 1:00 PM)
              </Typography>
              <Typography variant="body2" color="textSecondary">
                • Tea Breaks: 15 minutes each
              </Typography>
              <Typography variant="body2" color="textSecondary">
                • Maximum 2 tea breaks per day
              </Typography>
              <Typography variant="body2" color="textSecondary">
                • All breaks are monitored and logged
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* New Leave Request Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Leave Request</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Leave Type</InputLabel>
                <Select
                  value={formData.leave_type}
                  label="Leave Type"
                  onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                >
                  <MenuItem value="vacation">Vacation</MenuItem>
                  <MenuItem value="sick">Sick Leave</MenuItem>
                  <MenuItem value="personal">Personal Leave</MenuItem>
                  <MenuItem value="emergency">Emergency Leave</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={formData.start_date}
                  onChange={(date) => setFormData({ ...formData, start_date: date })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={formData.end_date}
                  onChange={(date) => setFormData({ ...formData, end_date: date })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitLeave} variant="contained">Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog.open}
        onClose={() => setApprovalDialog({ open: false, request: null, action: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {approvalDialog.action === 'approved' ? 'Approve' : 'Reject'} Leave Request
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Remarks (Optional)"
            value={approvalDialog.remarks || ''}
            onChange={(e) => setApprovalDialog({ ...approvalDialog, remarks: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog({ open: false, request: null, action: '' })}>
            Cancel
          </Button>
          <Button
            onClick={handleApproveReject}
            variant="contained"
            color={approvalDialog.action === 'approved' ? 'success' : 'error'}
          >
            {approvalDialog.action === 'approved' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LeaveRequests;
